import type { FocusableElement } from '@react-types/shared';
import type { FC } from 'react';

import { ButtonLink, FullOverlayLoader } from '@coop/components';
import classNames from 'classnames';
import { useEffect } from 'react';

import type { MyCoopProfileMenuItem } from '../../../episerver/api/localContentApi';

import { appConfig } from '../../../../appConfig';
import { ga4NavigationTracking } from '../../../common/tracking/ga4/navigation';
import { ga4UserTracking } from '../../../common/tracking/ga4/user';
import { useCurrentEpiPage } from '../../../episerver/components/hooks/useCurrentEpiPage';
import ProfileMenuListBonus2026 from '../../../myCoop/profileMenu/ProfileMenuListBonus2026/ProfileMenuListBonus2026';
import MenuHeaderBonus2026 from './MenuHeaderBonus2026/MenuHeaderBonus2026';
import { useMyCoopProfileMenu } from './MyCoopProfileFlyIn.hooks';
import styles from './MyCoopProfileFlyIn.module.scss';

interface MyCoopProfileMenuProps {
    handleListItemClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    navigationSlot?: React.ReactNode;
}
const getParentName = (menuItems: MyCoopProfileMenuItem[] | undefined, selectedPageId: number) => {
    if (menuItems === undefined) return null;

    const parentMenu = menuItems.find((menu) =>
        menu?.menuItemChildren?.some((child) => child.id === selectedPageId),
    );

    return parentMenu ? parentMenu.name : null;
};

const MyCoopProfileMenu: FC<MyCoopProfileMenuProps> = (props) => {
    const {
        data: pages,
        isSuccess: menuIsSuccess,
        isLoading: menuIsLoading,
    } = useMyCoopProfileMenu();

    const currentPage = useCurrentEpiPage();

    const menuPages = pages;

    const handleLinkClick = (e: React.MouseEvent<FocusableElement>) => {
        ga4NavigationTracking.profileMenu.click(
            (e.currentTarget as HTMLElement).innerText,
            (e.currentTarget as HTMLAnchorElement).href,
        );
    };

    const handleLogoutLinkClick = (e: React.MouseEvent<FocusableElement>) => {
        ga4UserTracking.logout();
        handleLinkClick(e);
    };

    const handleListItemClick = (
        e: React.MouseEvent<HTMLAnchorElement>,
        item: MyCoopProfileMenuItem,
    ) => {
        props.handleListItemClick?.(e);
        ga4NavigationTracking.profileMenuWalletInteractionTrigger.click(
            getParentName(pages, item.id) ?? e.currentTarget.innerText,
            getParentName(pages, item.id) === null ? undefined : e.currentTarget.innerText,
            'profile menu',
            e.currentTarget.innerText,
            e.currentTarget.href,
        );
    };

    const handleItemOpenClick = (item: MyCoopProfileMenuItem) => {
        ga4NavigationTracking.profileMenuWalletInteractionTrigger.open.level1Open(
            item.name,
            'profile menu',
        );
    };

    const handleItemCloseClick = (item: MyCoopProfileMenuItem) => {
        ga4NavigationTracking.profileMenuWalletInteractionTrigger.close.level1Close(
            item.name,
            'profile menu',
        );
    };

    useEffect(() => {
        ga4NavigationTracking.profileMenu.open();
        return () => {
            ga4NavigationTracking.profileMenu.close();
        };
    }, []);

    return (
        <>
            <MenuHeaderBonus2026
                navigationSlot={props.navigationSlot}
                myBonusOverviewPageUrl={appConfig.coopSettings.myCoop.myBonusOverviewPageUrl}
                handleLinkClick={handleLinkClick}
            />

            <div className={styles.Content}>
                <FullOverlayLoader isLoading={menuIsLoading}>
                    {menuIsSuccess && (
                        <>
                            {menuPages !== undefined && (
                                <ProfileMenuListBonus2026
                                    pages={menuPages}
                                    activePageId={currentPage?.data?.contentLink?.id}
                                    onItemClick={handleListItemClick}
                                    onItemOpen={handleItemOpenClick}
                                    onItemClose={handleItemCloseClick}
                                />
                            )}
                            <div className={classNames(styles.Footer, styles.FooterBonus2026)}>
                                <ButtonLink
                                    theme={'subtle'}
                                    href={appConfig.coopSettings.logoutUrl}
                                    onClick={handleLogoutLinkClick}
                                    fullWidth
                                >
                                    Logga ut
                                </ButtonLink>
                            </div>
                        </>
                    )}
                </FullOverlayLoader>
            </div>
        </>
    );
};

export default MyCoopProfileMenu;
